/**
 * pages-plan.ts — หน้าแพ็กเกจและการอัปเกรด (paywall UI)
 *
 * แสดงแพ็กเกจปัจจุบัน + โควตาที่ใช้ไป + ตารางเปรียบเทียบแพ็กเกจ (จาก PLAN_LIMITS)
 * การจ่ายเงินจริงยังไม่เชื่อม gateway (external boundary) — ปุ่มอัปเกรด = ติดต่อทีมงาน
 */
import { PLAN_LIMITS, PLAN_ORDER, MODULE_LABELS, type Plan, type GatedModule } from '@rrp/shared';
import type { Page } from './router.js';
import { getTenant } from './state.js';
import { esc } from './ui.js';
import * as api from './api.js';

function gb(bytes: number): string {
  return `${Math.round(bytes / 1024 / 1024 / 1024)} GB`;
}
function limNum(v: number | null): string {
  return v === null ? 'ไม่จำกัด' : String(v);
}
function modulesList(mods: GatedModule[]): string {
  if (mods.length === 0) return '<span class="muted">—</span>';
  return mods.map((m) => esc(MODULE_LABELS[m])).join(', ');
}

/** การ์ดแพ็กเกจหนึ่งใบ */
function planCard(plan: Plan, current: string | null): string {
  const l = PLAN_LIMITS[plan];
  const isCurrent = plan === current;
  return `<div class="plan-card${isCurrent ? ' current' : ''}">
    <div class="plan-name">${esc(l.label)}${isCurrent ? ' <span class="pill ok">แพ็กเกจปัจจุบัน</span>' : ''}</div>
    <div class="plan-price">${l.pricePerMonth === 0 ? 'ฟรี' : `฿${l.pricePerMonth.toLocaleString()}`}<span class="muted">/เดือน</span></div>
    <ul class="plan-feats">
      <li>กิจการ: ${limNum(l.businesses)}</li>
      <li>ร้าน: ${limNum(l.shops)}</li>
      <li>ผู้ใช้: ${limNum(l.users)}</li>
      <li>ประวัติย้อนหลัง: ${l.historyMonths === null ? 'ไม่จำกัด' : `${l.historyMonths} เดือน`}</li>
      <li>พื้นที่ไฟล์: ${gb(l.storageBytes)}</li>
      <li>ช่องทางอัตโนมัติ: ${l.autoChannels.length}</li>
      <li>โมดูลขั้นสูง: ${modulesList(l.modules)}</li>
    </ul>
    ${
      isCurrent
        ? '<button class="mini" disabled>กำลังใช้งาน</button>'
        : `<button class="mini plan-upgrade" data-plan="${plan}">เลือกแพ็กเกจนี้</button>`
    }
  </div>`;
}

export const planPage: Page = {
  title: 'แพ็กเกจและการใช้งาน',
  render() {
    const cards = PLAN_ORDER.map((p) => planCard(p, null)).join('');
    return `
      <div class="topbar"><h1>แพ็กเกจและการใช้งาน</h1></div>
      <div id="plan-current" class="card"><p class="muted">กำลังโหลดแพ็กเกจปัจจุบัน…</p></div>
      <h3 style="margin:18px 0 8px">เปรียบเทียบแพ็กเกจ</h3>
      <p class="foot-note">ราคาเป็นค่าตั้งต้น (ยังไม่รวม VAT) — ยังไม่เปิดชำระเงินออนไลน์ กด "เลือกแพ็กเกจนี้" เพื่อแจ้งความต้องการ</p>
      <div class="plan-grid">${cards}</div>`;
  },
  mount(root) {
    root.querySelectorAll<HTMLButtonElement>('.plan-upgrade').forEach((b) =>
      b.addEventListener('click', () => {
        const l = PLAN_LIMITS[b.dataset['plan'] as Plan];
        alert(
          `ต้องการอัปเกรดเป็นแพ็กเกจ "${l.label}"\n\nยังไม่เปิดชำระเงินออนไลน์ในช่วง demo — กรุณาติดต่อทีมงานเพื่อเปิดใช้งาน`,
        );
      }),
    );
    const cur = root.querySelector<HTMLElement>('#plan-current')!;
    if (!getTenant()) {
      cur.innerHTML =
        '<p class="muted">เข้าสู่ระบบด้วยบัญชีข้อมูลจริงเพื่อดูแพ็กเกจปัจจุบันและโควตาที่ใช้ไป</p>';
      // ยังแสดงตารางเปรียบเทียบให้ดูได้
      renderCompare(root, null);
      return;
    }
    void api
      .getPlan()
      .then((p) => {
        const usedShops = p.usage.shops ?? 0;
        const usedGb = ((p.usage.storageBytes ?? 0) / 1024 / 1024 / 1024).toFixed(2);
        cur.innerHTML = `
          <h3>แพ็กเกจปัจจุบัน: ${esc(p.limits.label)}
            ${p.status ? `<span class="pill">${esc(statusLabel(p.status))}</span>` : ''}</h3>
          <div class="kpis">
            <div class="kpi"><div class="label">ร้านที่เชื่อม</div><div class="value">${usedShops}${
              p.limits.shops === null ? '' : ` / ${p.limits.shops}`
            }</div></div>
            <div class="kpi"><div class="label">พื้นที่ไฟล์ใช้ไป</div><div class="value">${usedGb} / ${gb(p.limits.storageBytes)}</div></div>
            <div class="kpi"><div class="label">ประวัติย้อนหลัง</div><div class="value">${
              p.limits.historyMonths === null ? 'ไม่จำกัด' : `${p.limits.historyMonths} เดือน`
            }</div></div>
          </div>`;
        renderCompare(root, p.plan);
      })
      .catch((e: unknown) => {
        cur.innerHTML = `<p class="error">${esc(e instanceof Error ? e.message : 'โหลดแพ็กเกจไม่สำเร็จ')}</p>`;
        renderCompare(root, null);
      });
  },
};

function renderCompare(root: HTMLElement, current: string | null): void {
  const grid = root.querySelector<HTMLElement>('.plan-grid');
  if (grid) grid.innerHTML = PLAN_ORDER.map((p) => planCard(p, current)).join('');
  // ผูก event ปุ่มใหม่หลัง re-render
  grid?.querySelectorAll<HTMLButtonElement>('.plan-upgrade').forEach((b) =>
    b.addEventListener('click', () => {
      const l = PLAN_LIMITS[b.dataset['plan'] as Plan];
      alert(
        `ต้องการอัปเกรดเป็นแพ็กเกจ "${l.label}"\n\nยังไม่เปิดชำระเงินออนไลน์ในช่วง demo — กรุณาติดต่อทีมงานเพื่อเปิดใช้งาน`,
      );
    }),
  );
}

function statusLabel(s: string): string {
  return (
    {
      trialing: 'กำลังทดลองใช้',
      active: 'ใช้งานอยู่',
      past_due: 'ค้างชำระ',
      canceled: 'ยกเลิกแล้ว',
    }[s] ?? s
  );
}
